import React, { ComponentType, memo, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import Legend from "@app/scene/map/enums/Legend";
import { MapLayer } from "@app/scene/map/store";
import { toggleLayerLegendExpandedState } from "@app/scene/map/store/actions";
import { toggleLegendExpandedState } from "@app/scene/map/store/legends/actions";
import { getLayerLegendsState, getLegendsState } from "@app/scene/map/store/selectors";
import LegendState from "@app/scene/map/types/LegendState";
import mapSettings from "@app/scene/map/utils/mapSettings";
import { getMe, getOrganizationSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import isUserLocationLayerAvailable from "../../utils/isUserLocationLayerAvailable";

import AlertBadgesLegendCard from "./components/AlertBadgesLegendCard";
import BoundariesLegendCard from "./components/BoundariesLegendCard";
import ColorKeyLegendCard from "./components/ColorKeyLegendCard";
import HeatMapLegendCard from "./components/HeatMapLegendCard/index";
import { BaseLegendCardProps } from "./components/LegendCard";
import ShapeKeyLegendCard from "./components/ShapeKeyLegendCard";
import TrafficLegendCard from "./components/TrafficLegendCard";
import UserLocationsLegendCard from "./components/UserLocationsLegendCard";
import styles from "./Legends.module.scss";

const LegendComponent: Record<Legend, ComponentType<BaseLegendCardProps>> = {
  [Legend.ALERTS]: AlertBadgesLegendCard,
  [Legend.BOUNDARIES]: BoundariesLegendCard,
  [Legend.COLOR_KEY]: ColorKeyLegendCard,
  [Legend.HEAT_MAP]: HeatMapLegendCard,
  [Legend.SHAPE_KEY]: ShapeKeyLegendCard,
  [Legend.TRAFFIC]: TrafficLegendCard,
  [Legend.USER_LOCATIONS]: UserLocationsLegendCard,
};

interface Props {
  allowedLegends?: Legend[];
  className?: string;
  isUserLayerAvailable: boolean;
  legendsState: Record<Legend, LegendState>;
  toggleLayerLegendVisibility: (payload: { expanded: boolean; name: MapLayer }) => void;
  toggleLegendVisibility: (payload: { expanded: boolean; name: Legend }) => void;
}

const Legends: React.FC<Props> = ({
  allowedLegends,
  className,
  isUserLayerAvailable,
  legendsState,
  toggleLayerLegendVisibility,
  toggleLegendVisibility,
}) => {
  const [activeLegend, setActiveLegend] = useState<Legend | undefined>(undefined);
  const allLegends = useMemo(
    () =>
      (Object.keys(legendsState) as Legend[])
        .filter((legend) => allowedLegends?.includes(legend) ?? true)
        .filter((legend) => legend !== Legend.USER_LOCATIONS || isUserLayerAvailable),
    // Check is disabled because of the spread operator, otherwise we have all we need
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedLegends, isUserLayerAvailable, ...Object.keys(legendsState)]
  );
  const Component = activeLegend ? LegendComponent[activeLegend] : null;

  const handleToggleExpandedState = useCallback(
    (expanded: boolean, legend: Legend, isLayer: boolean | undefined) => {
      setActiveLegend(legend === activeLegend ? undefined : legend);
      if (isLayer) {
        toggleLayerLegendVisibility({ expanded, name: legend as MapLayer });
      } else {
        toggleLegendVisibility({ expanded, name: legend });
      }
    },
    [toggleLayerLegendVisibility, toggleLegendVisibility, setActiveLegend, activeLegend]
  );

  const [expandedHeight, setExpandedHeight] = useState(0);
  const [resizing, setResizing] = useState(false);
  // We don't need any fancy transitions when switching between tabs. Only when first expanding.
  const [noTransitionClassName, setNoTransitionClassName] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!activeLegend) {
      setExpandedHeight(0);
      setNoTransitionClassName(undefined);
    }
  }, [activeLegend]);
  const handleAnimationEnd = useCallback(() => {
    if (activeLegend) {
      setExpandedHeight(mapSettings.getKeyToolbarHeight());
      setNoTransitionClassName(styles.noTransition);
    }
  }, [activeLegend]);

  return (
    <div className={cn(styles.container, className)}>
      <div
        className={cn(styles.expandedLegends, { [styles.hasActiveLegend]: !!activeLegend })}
        onTransitionEnd={handleAnimationEnd}
        style={{
          // this is needed to avoid flickering or height changes when switching between active tabs
          minHeight: `${expandedHeight}px`,
          // do not play animations when element is active and height is settled. This is needed
          // to allow smooth legend resizing
          transition: resizing ? `min-height, 0s` : undefined,
        }}
      >
        {activeLegend && Component && (
          <Component
            className={noTransitionClassName}
            defaultHeight={mapSettings.getKeyToolbarHeight()}
            expanded
            isLayer={legendsState[activeLegend].isLayer}
            onHeightChange={setExpandedHeight}
            onResize={setResizing}
            onToggle={(expanded: boolean) =>
              handleToggleExpandedState(expanded, activeLegend, legendsState[activeLegend].isLayer)
            }
            visible={legendsState[activeLegend].visible}
          />
        )}
      </div>
      <Interactive>
        <div
          className={cn(styles.collapsedLegends, {
            [styles.hasActiveLegend]: !!activeLegend,
          })}
        >
          {allLegends.map((legend) => {
            const Component = LegendComponent[legend]!;
            return (
              <Component
                active={activeLegend === legend}
                expanded={false}
                key={legend}
                onToggle={(expanded: boolean) =>
                  handleToggleExpandedState(expanded, legend, legendsState[legend].isLayer)
                }
                visible={legendsState[legend].visible}
              />
            );
          })}
        </div>
      </Interactive>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isUserLayerAvailable: isUserLocationLayerAvailable(getMe(state)!, getOrganizationSettings(state)),
  legendsState: { ...getLegendsState(state), ...getLayerLegendsState(state) },
});

const mapDispatchToProps = {
  toggleLayerLegendVisibility: toggleLayerLegendExpandedState,
  toggleLegendVisibility: toggleLegendExpandedState,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(Legends));
